import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getPlanTemplates,
  getPlanTemplateById,
  createPlanTemplate,
  updatePlanTemplate,
  deletePlanTemplate,
  clonePlanTemplate,
  publishPlanTemplate,
  unpublishPlanTemplate,
  getRoleRequirements,
  createRoleRequirement,
  updateRoleRequirement,
  deleteRoleRequirement,
  getStationRequirements,
  createStationRequirement,
  updateStationRequirement,
  deleteStationRequirement,
  getPlanFloorplans,
  getPlanFloorplan,
  createPlanFloorplan,
  updatePlanFloorplan,
  deletePlanFloorplan,
  getLabelTemplates,
  createLabelTemplate,
  updateLabelTemplate,
  deleteLabelTemplate,
  getApplicableSites,
  createApplicableSite,
  updateApplicableSite,
  deleteApplicableSite,
  getReunificationSites,
  getReunificationSiteById,
  getReunificationEvents,
  getReunificationEventById,
  getEventStudents,
  getEventStudentById,
  createEventStudent,
  updateEventStudent,
  getEventGuardians,
  getEventStaff,
  getPlanningStudents,
  getPlanStaffAssignments,
  createPlanStaffAssignment,
  updatePlanStaffAssignment,
  deletePlanStaffAssignment,
  generateConcretePlan,
  activateConcretePlan,
  activateReunificationForAlert,
  getConcretePlanReadiness,
  pauseEvent,
  resumeEvent,
  completeEvent,
  updateStudentStatus,
  bulkAccountStudents,
  releaseStudent,
  checkInGuardian,
  verifyGuardian,
  denyGuardian,
  createEventGuardian,
  updateEventGuardian,
  createEventStaff,
  updateEventStaff,
  checkInStaff,
  releaseStaff,
  getEventLogs,
  getEventMetrics,
  getEventTimeline,
  getDistrictReadiness,
  generateEventReport,
  downloadEventReport,
  getConfigurableTypes,
  createConfigurableType,
  updateConfigurableType,
  deleteConfigurableType,
  getConfigurableTypeById,
  getNoteTypes,
  getReunificationNotes,
  createReunificationNote,
  updateReunificationNote,
  deleteReunificationNote,
} from '../../services/reunification.service';
import type {
  PlanTemplateFilters,
  PlanTemplateWrite,
  PlanTemplateDetail,
  PlanTemplateListResponse,
  PlanRoleRequirementWrite,
  PlanStationRequirementWrite,
  PlanFloorplanWrite,
  PlanLabelTemplateWrite,
  PlanApplicableSiteWrite,
  SiteFilters,
  ReunificationEventFilters,
  ReunificationEventDetail,
  ReunificationEventListResponse,
  StudentFilters,
  StudentStatus,
  StudentListResponse,
  StudentReunification,
  StudentReunificationWrite,
  GuardianFilters,
  StaffFilters,
  EventLogFilters,
  EventStaffAssignmentWrite,
  GuardianReunificationWrite,
  PlanningStudentFilters,
  PlanStaffAssignmentFilters,
  PlanStaffAssignmentWrite,
  TypeCategory,
  ConfigurableTypeWrite,
  ReportSection,
  ReunificationNoteFilters,
  ReunificationNoteWrite,
} from '../../types/ReunificationTypes';

// =============================================================================
// Query Key Factories
// =============================================================================

export const planTemplateKeys = {
  all: ['planTemplates'] as const,
  lists: () => [...planTemplateKeys.all, 'list'] as const,
  list: (filters: PlanTemplateFilters) =>
    [...planTemplateKeys.lists(), filters] as const,
  details: () => [...planTemplateKeys.all, 'detail'] as const,
  detail: (id: string) => [...planTemplateKeys.details(), id] as const,
};

export const reunificationSiteKeys = {
  all: ['reunificationSites'] as const,
  lists: () => [...reunificationSiteKeys.all, 'list'] as const,
  list: (filters: SiteFilters) =>
    [...reunificationSiteKeys.lists(), filters] as const,
  details: () => [...reunificationSiteKeys.all, 'detail'] as const,
  detail: (id: string) => [...reunificationSiteKeys.details(), id] as const,
};

export const reunificationEventKeys = {
  all: ['reunificationEvents'] as const,
  lists: () => [...reunificationEventKeys.all, 'list'] as const,
  list: (filters: ReunificationEventFilters) =>
    [...reunificationEventKeys.lists(), filters] as const,
  details: () => [...reunificationEventKeys.all, 'detail'] as const,
  detail: (id: string) => [...reunificationEventKeys.details(), id] as const,
  students: (eventId: string, filters: StudentFilters) =>
    [...reunificationEventKeys.detail(eventId), 'students', filters] as const,
  student: (eventId: string, studentId: string) =>
    [...reunificationEventKeys.detail(eventId), 'student', studentId] as const,
  guardians: (eventId: string, filters: GuardianFilters) =>
    [...reunificationEventKeys.detail(eventId), 'guardians', filters] as const,
  staff: (eventId: string, filters: StaffFilters) =>
    [...reunificationEventKeys.detail(eventId), 'staff', filters] as const,
  logs: (eventId: string, filters?: EventLogFilters) =>
    [...reunificationEventKeys.detail(eventId), 'logs', filters ?? {}] as const,
  metrics: (eventId: string) =>
    [...reunificationEventKeys.detail(eventId), 'metrics'] as const,
  timeline: (eventId: string) =>
    [...reunificationEventKeys.detail(eventId), 'timeline'] as const,
  report: (eventId: string) =>
    [...reunificationEventKeys.detail(eventId), 'report'] as const,
  notes: (eventId: string, filters: ReunificationNoteFilters) =>
    [...reunificationEventKeys.detail(eventId), 'notes', filters] as const,
};

// =============================================================================
// Note Types — ADR-121
// =============================================================================

export const noteTypeKeys = {
  all: ['noteTypes'] as const,
  list: () => [...noteTypeKeys.all, 'list'] as const,
};

export const reportingKeys = {
  all: ['reporting'] as const,
  districtReadiness: () => [...reportingKeys.all, 'districtReadiness'] as const,
};

// =============================================================================
// Plan Template Hooks
// =============================================================================

export function usePlanTemplates(filters: PlanTemplateFilters) {
  return useQuery<PlanTemplateListResponse, Error>({
    queryKey: planTemplateKeys.list(filters),
    queryFn: () => getPlanTemplates(filters),
    placeholderData: (previousData) => previousData,
  });
}

export function usePlanTemplate(id: string) {
  return useQuery<PlanTemplateDetail, Error>({
    queryKey: planTemplateKeys.detail(id),
    queryFn: () => getPlanTemplateById(id),
    enabled: !!id,
  });
}

export function useCreatePlanTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createPlanTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planTemplateKeys.lists() });
    },
  });
}

export function useUpdatePlanTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<PlanTemplateWrite>;
    }) => updatePlanTemplate(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: planTemplateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: planTemplateKeys.detail(id) });
    },
  });
}

export function useDeletePlanTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deletePlanTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planTemplateKeys.lists() });
    },
  });
}

export function useClonePlanTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    // Accept either a bare public_id (preserves legacy callers) or
    // {id, name} so the AddPlanDialog can name the resulting clone.
    mutationFn: (vars: string | { id: string; name?: string }) =>
      typeof vars === 'string'
        ? clonePlanTemplate(vars)
        : clonePlanTemplate(vars.id, vars.name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planTemplateKeys.lists() });
    },
  });
}

export function usePublishPlanTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: publishPlanTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planTemplateKeys.lists() });
    },
  });
}

export function useUnpublishPlanTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: unpublishPlanTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planTemplateKeys.lists() });
    },
  });
}

// =============================================================================
// Plan Role Requirement Hooks
// =============================================================================

export const roleRequirementKeys = {
  all: (templateId: string) =>
    [...planTemplateKeys.detail(templateId), 'roleRequirements'] as const,
  list: (templateId: string) =>
    [...roleRequirementKeys.all(templateId), 'list'] as const,
};

export function useRoleRequirements(templateId: string) {
  return useQuery({
    queryKey: roleRequirementKeys.list(templateId),
    queryFn: () => getRoleRequirements(templateId),
    enabled: !!templateId,
  });
}

export function useCreateRoleRequirement(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: PlanRoleRequirementWrite) =>
      createRoleRequirement(templateId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: roleRequirementKeys.list(templateId),
      });
    },
  });
}

export function useUpdateRoleRequirement(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<PlanRoleRequirementWrite>;
    }) => updateRoleRequirement(templateId, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: roleRequirementKeys.list(templateId),
      });
    },
  });
}

export function useDeleteRoleRequirement(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteRoleRequirement(templateId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: roleRequirementKeys.list(templateId),
      });
    },
  });
}

// =============================================================================
// Plan Station Requirement Hooks
// =============================================================================

export const stationRequirementKeys = {
  all: (templateId: string) =>
    [...planTemplateKeys.detail(templateId), 'stationRequirements'] as const,
  list: (templateId: string) =>
    [...stationRequirementKeys.all(templateId), 'list'] as const,
};

export function useStationRequirements(templateId: string) {
  return useQuery({
    queryKey: stationRequirementKeys.list(templateId),
    queryFn: () => getStationRequirements(templateId),
    enabled: !!templateId,
  });
}

export function useCreateStationRequirement(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: PlanStationRequirementWrite) =>
      createStationRequirement(templateId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: stationRequirementKeys.list(templateId),
      });
    },
  });
}

export function useUpdateStationRequirement(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<PlanStationRequirementWrite>;
    }) => updateStationRequirement(templateId, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: stationRequirementKeys.list(templateId),
      });
    },
  });
}

export function useDeleteStationRequirement(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteStationRequirement(templateId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: stationRequirementKeys.list(templateId),
      });
    },
  });
}

// =============================================================================
// Plan Floorplan Hooks (abstract floorplan editor)
// =============================================================================

export const planFloorplanKeys = {
  all: (templateId: string) =>
    [...planTemplateKeys.detail(templateId), 'floorplans'] as const,
  list: (templateId: string) =>
    [...planFloorplanKeys.all(templateId), 'list'] as const,
  detail: (templateId: string, floorplanId: string) =>
    [...planFloorplanKeys.all(templateId), 'detail', floorplanId] as const,
};

export function usePlanFloorplans(templateId: string, enabled = true) {
  return useQuery({
    queryKey: planFloorplanKeys.list(templateId),
    queryFn: () => getPlanFloorplans(templateId),
    enabled: !!templateId && enabled,
  });
}

export function usePlanFloorplan(
  templateId: string,
  floorplanId: string | null,
) {
  return useQuery({
    queryKey: planFloorplanKeys.detail(templateId, floorplanId ?? ''),
    queryFn: () => getPlanFloorplan(templateId, floorplanId as string),
    enabled: !!templateId && !!floorplanId,
  });
}

/** Invalidate floorplans + the template detail (which nests floorplans). */
export function useInvalidatePlanFloorplans(templateId: string) {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({
      queryKey: planFloorplanKeys.all(templateId),
    });
    queryClient.invalidateQueries({
      queryKey: planTemplateKeys.detail(templateId),
    });
  };
}

export function useCreatePlanFloorplan(templateId: string) {
  const invalidate = useInvalidatePlanFloorplans(templateId);
  return useMutation({
    mutationFn: (data: PlanFloorplanWrite) =>
      createPlanFloorplan(templateId, data),
    onSuccess: invalidate,
  });
}

export function useUpdatePlanFloorplan(templateId: string) {
  const invalidate = useInvalidatePlanFloorplans(templateId);
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<PlanFloorplanWrite>;
    }) => updatePlanFloorplan(templateId, id, data),
    onSuccess: invalidate,
  });
}

export function useDeletePlanFloorplan(templateId: string) {
  const invalidate = useInvalidatePlanFloorplans(templateId);
  return useMutation({
    mutationFn: (id: string) => deletePlanFloorplan(templateId, id),
    onSuccess: invalidate,
  });
}

// =============================================================================
// Label Template Hooks
// =============================================================================

export const labelTemplateKeys = {
  all: (templateId: string) =>
    [...planTemplateKeys.detail(templateId), 'labelTemplates'] as const,
  list: (templateId: string) =>
    [...labelTemplateKeys.all(templateId), 'list'] as const,
};

export function useLabelTemplates(templateId: string) {
  return useQuery({
    queryKey: labelTemplateKeys.list(templateId),
    queryFn: () => getLabelTemplates(templateId),
    enabled: !!templateId,
  });
}

export function useCreateLabelTemplate(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: PlanLabelTemplateWrite) =>
      createLabelTemplate(templateId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: labelTemplateKeys.list(templateId),
      });
    },
  });
}

export function useUpdateLabelTemplate(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<PlanLabelTemplateWrite>;
    }) => updateLabelTemplate(templateId, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: labelTemplateKeys.list(templateId),
      });
    },
  });
}

export function useDeleteLabelTemplate(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteLabelTemplate(templateId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: labelTemplateKeys.list(templateId),
      });
    },
  });
}

// =============================================================================
// Applicable Site Hooks
// =============================================================================

export const applicableSiteKeys = {
  all: (templateId: string) =>
    [...planTemplateKeys.detail(templateId), 'applicableSites'] as const,
  list: (templateId: string) =>
    [...applicableSiteKeys.all(templateId), 'list'] as const,
};

export function useApplicableSites(templateId: string) {
  return useQuery({
    queryKey: applicableSiteKeys.list(templateId),
    queryFn: () => getApplicableSites(templateId),
    enabled: !!templateId,
  });
}

export function useCreateApplicableSite(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: PlanApplicableSiteWrite) =>
      createApplicableSite(templateId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: applicableSiteKeys.list(templateId),
      });
      queryClient.invalidateQueries({
        queryKey: planTemplateKeys.detail(templateId),
      });
    },
  });
}

export function useUpdateApplicableSite(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      siteId,
      data,
    }: {
      siteId: string;
      data: Partial<PlanApplicableSiteWrite>;
    }) => updateApplicableSite(templateId, siteId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: applicableSiteKeys.list(templateId),
      });
      queryClient.invalidateQueries({
        queryKey: planTemplateKeys.detail(templateId),
      });
    },
  });
}

export function useDeleteApplicableSite(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (siteId: string) => deleteApplicableSite(templateId, siteId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: applicableSiteKeys.list(templateId),
      });
      queryClient.invalidateQueries({
        queryKey: planTemplateKeys.detail(templateId),
      });
    },
  });
}

// =============================================================================
// Site Hooks
// =============================================================================

export function useReunificationSites(filters: SiteFilters) {
  return useQuery({
    queryKey: reunificationSiteKeys.list(filters),
    queryFn: () => getReunificationSites(filters),
    placeholderData: (previousData) => previousData,
  });
}

export function useReunificationSite(id: string) {
  return useQuery({
    queryKey: reunificationSiteKeys.detail(id),
    queryFn: () => getReunificationSiteById(id),
    enabled: !!id,
  });
}

// =============================================================================
// Reunification Event Hooks
// =============================================================================

export function useReunificationEvents(filters: ReunificationEventFilters) {
  return useQuery<ReunificationEventListResponse, Error>({
    queryKey: reunificationEventKeys.list(filters),
    queryFn: () => getReunificationEvents(filters),
    placeholderData: (previousData) => previousData,
  });
}

export function useReunificationEvent(id: string) {
  return useQuery<ReunificationEventDetail, Error>({
    queryKey: reunificationEventKeys.detail(id),
    queryFn: () => getReunificationEventById(id),
    enabled: !!id,
  });
}

// =============================================================================
// Event Sub-resource Hooks
// =============================================================================

export function useEventStudents(eventId: string, filters: StudentFilters) {
  return useQuery<StudentListResponse, Error>({
    queryKey: reunificationEventKeys.students(eventId, filters),
    queryFn: () => getEventStudents(eventId, filters),
    enabled: !!eventId,
    placeholderData: (previousData) => previousData,
  });
}

/**
 * Fetch a single student detail row for the drawer's edit form.
 *
 * Note: every successful fetch writes a `student.viewed` row to
 * `EventAssignmentLog` on the backend (FERPA access-log). Enable
 * only when the drawer is actually open — don't pre-fetch hidden
 * rows, or the log will fill with phantom "viewed" entries.
 */
export function useEventStudent(
  eventId: string,
  studentId: string | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery<StudentReunification, Error>({
    queryKey: reunificationEventKeys.student(eventId, studentId ?? ''),
    queryFn: () => getEventStudentById(eventId, studentId as string),
    enabled: !!eventId && !!studentId && options?.enabled !== false,
  });
}

export function useEventGuardians(eventId: string, filters: GuardianFilters) {
  return useQuery({
    queryKey: reunificationEventKeys.guardians(eventId, filters),
    queryFn: () => getEventGuardians(eventId, filters),
    enabled: !!eventId,
    placeholderData: (previousData) => previousData,
  });
}

export function useEventStaff(eventId: string, filters: StaffFilters) {
  return useQuery({
    queryKey: reunificationEventKeys.staff(eventId, filters),
    queryFn: () => getEventStaff(eventId, filters),
    enabled: !!eventId,
    placeholderData: (previousData) => previousData,
  });
}

// =============================================================================
// Pre-Event Planning: Students (Roster)
// =============================================================================

export const planningStudentKeys = {
  all: ['planningStudents'] as const,
  lists: () => [...planningStudentKeys.all, 'list'] as const,
  list: (filters: PlanningStudentFilters) =>
    [...planningStudentKeys.lists(), filters] as const,
};

export function usePlanningStudents(filters: PlanningStudentFilters) {
  return useQuery({
    queryKey: planningStudentKeys.list(filters),
    queryFn: () => getPlanningStudents(filters),
    placeholderData: (previousData) => previousData,
  });
}

// =============================================================================
// Staff Role Pre-Assignment Hooks
// =============================================================================

export const staffRoleKeys = {
  all: ['staffRoles'] as const,
  lists: () => [...staffRoleKeys.all, 'list'] as const,
  list: (filters: PlanStaffAssignmentFilters) =>
    [...staffRoleKeys.lists(), filters] as const,
};

export function usePlanStaffAssignments(
  filters: PlanStaffAssignmentFilters,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: staffRoleKeys.list(filters),
    queryFn: () => getPlanStaffAssignments(filters),
    placeholderData: (previousData) => previousData,
    enabled: options?.enabled,
  });
}

export function useCreatePlanStaffAssignment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createPlanStaffAssignment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: staffRoleKeys.lists() });
    },
  });
}

export function useUpdatePlanStaffAssignment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<PlanStaffAssignmentWrite>;
    }) => updatePlanStaffAssignment(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: staffRoleKeys.lists() });
    },
  });
}

export function useDeletePlanStaffAssignment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deletePlanStaffAssignment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: staffRoleKeys.lists() });
    },
  });
}

// =============================================================================
// Concrete Plan: Generate / Activate Mutations
// =============================================================================

export function useGeneratePlan(planId: string, siteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => generateConcretePlan(planId, siteId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: planTemplateKeys.detail(planId),
      });
      queryClient.invalidateQueries({ queryKey: planTemplateKeys.lists() });
    },
  });
}

export function useActivatePlan(planId: string, siteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (alertId: number) =>
      activateConcretePlan(planId, siteId, alertId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: planTemplateKeys.detail(planId),
      });
      queryClient.invalidateQueries({
        queryKey: reunificationEventKeys.lists(),
      });
    },
  });
}

export function useActivateForAlert() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (alertId: number) => activateReunificationForAlert(alertId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: reunificationEventKeys.lists(),
      });
    },
  });
}

// =============================================================================
// Event Lifecycle Mutations
// =============================================================================

export function usePauseEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: pauseEvent,
    onSuccess: (_, eventId) => {
      queryClient.invalidateQueries({
        queryKey: reunificationEventKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: reunificationEventKeys.detail(eventId),
      });
    },
  });
}

export function useResumeEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: resumeEvent,
    onSuccess: (_, eventId) => {
      queryClient.invalidateQueries({
        queryKey: reunificationEventKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: reunificationEventKeys.detail(eventId),
      });
    },
  });
}

export function useCompleteEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: completeEvent,
    onSuccess: (_, eventId) => {
      queryClient.invalidateQueries({
        queryKey: reunificationEventKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: reunificationEventKeys.detail(eventId),
      });
    },
  });
}

// =============================================================================
// Student Status Mutations
// =============================================================================

export function useUpdateStudentStatus(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      studentId,
      data,
    }: {
      studentId: string;
      data: { status: StudentStatus };
    }) => updateStudentStatus(eventId, studentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: reunificationEventKeys.detail(eventId),
      });
      queryClient.invalidateQueries({
        queryKey: [...reunificationEventKeys.detail(eventId), 'students'],
      });
    },
  });
}

/**
 * Create a new StudentReunification row on an active event.
 *
 * Mirrors `useCreateEventStaff` but targets the student
 * viewset. The backend gates the call by `CanOperateStudentCheckIn`
 * (operators / supervisors / coordinators / administrators) and
 * writes a FERPA audit-log row on success.
 */
export function useCreateEventStudent(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: StudentReunificationWrite) =>
      createEventStudent(eventId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...reunificationEventKeys.detail(eventId), 'students'],
      });
    },
  });
}

/**
 * PATCH the writable detail fields of a StudentReunification row:
 * notes, medical_notes, special_instructions, requires_leo_interview,
 * requires_counseling, supervising_staff.
 *
 * Status workflow still routes through `useUpdateStudentStatus` —
 * that path carries the WebSocket broadcasts. The backend writes a
 * `student.edited` row to `EventAssignmentLog` on success;
 * `medical_notes` writes require an additional permission and 403
 * if the user lacks it.
 */
export function useUpdateEventStudent(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      studentId,
      data,
    }: {
      studentId: string;
      data: Partial<StudentReunificationWrite>;
    }) => updateEventStudent(eventId, studentId, data),
    onSuccess: (_, { studentId }) => {
      queryClient.invalidateQueries({
        queryKey: [...reunificationEventKeys.detail(eventId), 'students'],
      });
      queryClient.invalidateQueries({
        queryKey: reunificationEventKeys.student(eventId, studentId),
      });
    },
  });
}

export function useBulkAccountStudents(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { student_ids?: string[] }) =>
      bulkAccountStudents(eventId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: reunificationEventKeys.detail(eventId),
      });
      queryClient.invalidateQueries({
        queryKey: [...reunificationEventKeys.detail(eventId), 'students'],
      });
    },
  });
}

export function useReleaseStudent(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      studentId,
      data,
    }: {
      studentId: string;
      data: { guardian_id: string; notes?: string };
    }) => releaseStudent(eventId, studentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: reunificationEventKeys.detail(eventId),
      });
      queryClient.invalidateQueries({
        queryKey: [...reunificationEventKeys.detail(eventId), 'students'],
      });
      queryClient.invalidateQueries({
        queryKey: [...reunificationEventKeys.detail(eventId), 'guardians'],
      });
    },
  });
}

// =============================================================================
// Guardian Check-in / Verification Mutations
// =============================================================================

export function useCheckInGuardian(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (guardianId: string) => checkInGuardian(eventId, guardianId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...reunificationEventKeys.detail(eventId), 'guardians'],
      });
    },
  });
}

/**
 * Create a new GuardianReunification on an active event.
 * Invalidates the event's guardian list so the panel reflects the new row
 * immediately. Backend writes an EventAssignmentLog `guardian_added` row.
 */
export function useCreateEventGuardian(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: GuardianReunificationWrite) =>
      createEventGuardian(eventId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...reunificationEventKeys.detail(eventId), 'guardians'],
      });
    },
  });
}

/**
 * PATCH an existing GuardianReunification (verification_method / id_type /
 * is_pre_verified / authorized_students). Workflow fields
 * (status / arrived_at / verified_at) flow through the dedicated check_in /
 * verify / deny / pre_verify hooks which carry the WebSocket broadcasts;
 * a raw status write would skip those.
 *
 * Backend writes a `guardian_updated` EventAssignmentLog row with before/
 * after values so the privacy audit-log captures `authorized_students`
 * deltas.
 */
export function useUpdateEventGuardian(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      guardianId,
      data,
    }: {
      guardianId: string;
      data: Partial<GuardianReunificationWrite>;
    }) => updateEventGuardian(eventId, guardianId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...reunificationEventKeys.detail(eventId), 'guardians'],
      });
    },
  });
}

export function useVerifyGuardian(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      guardianId,
      data,
    }: {
      guardianId: string;
      data: { id_type?: string; id_number?: string };
    }) => verifyGuardian(eventId, guardianId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...reunificationEventKeys.detail(eventId), 'guardians'],
      });
    },
  });
}

export function useDenyGuardian(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      guardianId,
      data,
    }: {
      guardianId: string;
      data: { reason?: string };
    }) => denyGuardian(eventId, guardianId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...reunificationEventKeys.detail(eventId), 'guardians'],
      });
    },
  });
}

// =============================================================================
// Staff CRUD + Check-in / Release Mutations
// =============================================================================

/**
 * Create a new EventStaffAssignment on an active event. Invalidates the
 * event's staff list query so the panel reflects the new row immediately.
 */
export function useCreateEventStaff(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: EventStaffAssignmentWrite) =>
      createEventStaff(eventId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...reunificationEventKeys.detail(eventId), 'staff'],
      });
    },
  });
}

/**
 * PATCH an existing EventStaffAssignment (role / station_assignment /
 * notes). Workflow fields (status / checked_in_at / released_at) flow
 * through `useCheckInStaff` / `useReleaseStaff` which carry the
 * WebSocket broadcast + chat-join side effects.
 */
export function useUpdateEventStaff(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      staffId,
      data,
    }: {
      staffId: string;
      data: Partial<EventStaffAssignmentWrite>;
    }) => updateEventStaff(eventId, staffId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...reunificationEventKeys.detail(eventId), 'staff'],
      });
    },
  });
}

export function useCheckInStaff(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (staffId: string) => checkInStaff(eventId, staffId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...reunificationEventKeys.detail(eventId), 'staff'],
      });
    },
  });
}

export function useReleaseStaff(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (staffId: string) => releaseStaff(eventId, staffId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...reunificationEventKeys.detail(eventId), 'staff'],
      });
    },
  });
}

// =============================================================================
// Event Audit Log Query
// =============================================================================

export function useEventLogs(eventId: string, filters?: EventLogFilters) {
  return useQuery({
    queryKey: reunificationEventKeys.logs(eventId, filters),
    queryFn: () => getEventLogs(eventId, filters),
    enabled: !!eventId,
    placeholderData: (previousData) => previousData,
  });
}

// =============================================================================
// Reporting & Analytics Hooks
// =============================================================================

export function useEventMetrics(eventId: string) {
  return useQuery({
    queryKey: reunificationEventKeys.metrics(eventId),
    queryFn: () => getEventMetrics(eventId),
    enabled: !!eventId,
    refetchInterval: 15_000,
  });
}

export function useEventTimeline(eventId: string) {
  return useQuery({
    queryKey: reunificationEventKeys.timeline(eventId),
    queryFn: () => getEventTimeline(eventId),
    enabled: !!eventId,
    refetchInterval: 10_000,
  });
}

export function useDistrictReadiness() {
  return useQuery({
    queryKey: reportingKeys.districtReadiness(),
    queryFn: () => getDistrictReadiness(),
  });
}

/**
 * Fetch the inline-display version of the event report (used by the
 * Report panel in the drawer). Defaults to ``include_pii=false`` so the
 * panel can render without elevated permissions (ADR-099 §Layer 5).
 *
 * For the file-download flow, use ``useDownloadEventReport`` (mutation)
 * — keeping them separate ensures the inline summary never triggers a
 * blob download, and the blob download never updates query cache.
 *
 */
export function useEventReport(
  eventId: string,
  options: {
    include_sections?: ReportSection[];
    include_pii?: boolean;
    redact_sensitive?: boolean;
    time_zone?: string;
  } = {},
) {
  return useQuery({
    queryKey: [...reunificationEventKeys.report(eventId), options],
    queryFn: () => generateEventReport(eventId, options),
    enabled: !!eventId,
  });
}

/**
 * Mutation hook for downloading the event report as a JSON file.
 *
 * Mutation (not query) because each invocation has the side-effect of
 * saving a file to disk + optionally writing a FERPA audit-log row on
 * the server (when ``include_pii=true``). React Query's mutation
 * semantics (manual ``mutate`` invocation, no cache write-back) match
 * that lifecycle cleanly.
 */
export function useDownloadEventReport(eventId: string) {
  return useMutation({
    mutationFn: (
      options: {
        include_sections?: ReportSection[];
        include_pii?: boolean;
        redact_sensitive?: boolean;
        time_zone?: string;
      } = {},
    ) => downloadEventReport(eventId, options),
  });
}

// =============================================================================
// Concrete Plan: Readiness Query
// =============================================================================

export const concretePlanKeys = {
  readiness: (templateId: string, siteId: string) =>
    [...planTemplateKeys.detail(templateId), 'readiness', siteId] as const,
};

export function useConcretePlanReadiness(
  templateId: string,
  siteId: string,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: concretePlanKeys.readiness(templateId, siteId),
    queryFn: () => getConcretePlanReadiness(templateId, siteId),
    enabled: (options?.enabled ?? true) && !!templateId && !!siteId,
  });
}

// =============================================================================
// Configurable Types
// =============================================================================

export const configurableTypeKeys = {
  all: ['configurableTypes'] as const,
  lists: () => [...configurableTypeKeys.all, 'list'] as const,
  list: (category: TypeCategory) =>
    [...configurableTypeKeys.lists(), category] as const,
};

export function useConfigurableTypes(typeCategory: TypeCategory) {
  return useQuery({
    queryKey: configurableTypeKeys.list(typeCategory),
    queryFn: () => getConfigurableTypes(typeCategory),
    enabled: !!typeCategory,
  });
}

export function useCreateConfigurableType(typeCategory: TypeCategory) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ConfigurableTypeWrite) =>
      createConfigurableType(typeCategory, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: configurableTypeKeys.list(typeCategory),
      });
    },
  });
}

export function useUpdateConfigurableType(typeCategory: TypeCategory) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<ConfigurableTypeWrite>;
    }) => updateConfigurableType(typeCategory, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: configurableTypeKeys.list(typeCategory),
      });
    },
  });
}

export function useDeleteConfigurableType(typeCategory: TypeCategory) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteConfigurableType(typeCategory, id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: configurableTypeKeys.list(typeCategory),
      });
    },
  });
}
export function useConfigurableTypeById(
  typeCategory: TypeCategory,
  id: string,
) {
  return useQuery({
    queryKey: [...configurableTypeKeys.list(typeCategory), id],
    queryFn: () => getConfigurableTypeById(typeCategory, id),
    enabled: !!typeCategory && !!id,
  });
}

// =============================================================================
// Reunification Notes Hooks — ADR-121 P3
// =============================================================================

/**
 * Fetch the catalog of `NoteType` rows (powers the notes panel type
 * selector). Backed by `NoteTypeViewSet.list()`. Typed
 * shape includes `visibility_tier` + `visibility_permission` for
 * per-row sensitivity rendering.
 *
 * Cache is global (not per-event) — note-types are configurable at
 * the district level, not per-event, so a single fetch serves every
 * panel mounted in the session.
 */
export function useNoteTypes() {
  return useQuery({
    queryKey: noteTypeKeys.list(),
    queryFn: () => getNoteTypes(),
    // NoteType rows change rarely; cache them aggressively to avoid
    // re-fetching every time the drawer opens a new subject.
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch reunification notes for an event, optionally filtered.
 *
 * Note: server-side visibility filter — the response omits any
 * note whose `note_type.visibility_tier` is not `public` and whose
 * `visibility_permission` the caller lacks. Do NOT layer a
 * client-side role check on top of this. See ADR-121 §Decision-2.
 */
export function useReunificationNotes(
  eventId: string,
  filters: ReunificationNoteFilters,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: reunificationEventKeys.notes(eventId, filters),
    queryFn: () => getReunificationNotes(eventId, filters),
    enabled: !!eventId && options?.enabled !== false,
    placeholderData: (previousData) => previousData,
  });
}

/**
 * Create a new note. Invalidates every notes query for the event so
 * the panel reflects the new row regardless of which subject filter
 * is currently in effect (the new note may belong to a different
 * subject than the active filter — invalidating broadly is
 * cheaper than reasoning about specific filter combinations).
 */
export function useCreateReunificationNote(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ReunificationNoteWrite) =>
      createReunificationNote(eventId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...reunificationEventKeys.detail(eventId), 'notes'],
      });
    },
  });
}

/**
 * PATCH an existing note's body (and/or type).
 */
export function useUpdateReunificationNote(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      noteId,
      data,
    }: {
      noteId: string;
      data: Partial<ReunificationNoteWrite>;
    }) => updateReunificationNote(eventId, noteId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...reunificationEventKeys.detail(eventId), 'notes'],
      });
    },
  });
}

/**
 * DELETE a note.
 */
export function useDeleteReunificationNote(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (noteId: string) => deleteReunificationNote(eventId, noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...reunificationEventKeys.detail(eventId), 'notes'],
      });
    },
  });
}
